import cx from 'classnames';
import { counter_PHOTOS } from 'mk/autogenerated/translations/BlogPostPhotos.5f5993a55a4ffbe7a861a823388a4650'
import { photoUrl } from 'mk/photo/photoUrl';
import { BlogPostEntity, ImageEntity } from 'mk2/apps/blogs/schemas';
import { photoDetailUrl } from 'mk2/apps/blogs/utils';
import Img from 'mk2/components/Img';
import Link from 'mk2/components/Link';
import { PhotoEntity } from 'mk2/schemas';
import { interpolate } from 'mk2/services/i18n';
import React from 'react';
import styles from './BlogPostPhotos.mscss';

const MAX_WIDTH = 640;
const MAX_HEIGHT = 480;

const showMagnifier = (photo: PhotoEntity) => photo.versions.includes('s1600x1600');

interface OwnProps {
    complaintsCounts: { [imageId: number]: number };
    inFeed: boolean;
    mobile: boolean;
    post: BlogPostEntity;
    showStrip: boolean;
}

const ResizedPhoto = ({ className, maxHeight, maxWidth, mobile, photo, responsiveIfBig }: { className: string, maxHeight: number, maxWidth: number, mobile: boolean, photo: PhotoEntity, responsiveIfBig: boolean }) => {
    const wscale = photo.width / maxWidth;
    const hscale = photo.height / maxHeight;
    const cropAxis = (hscale >= wscale) ? 'width' : 'height';

    let imgWidth;
    let imgHeight;
    if (cropAxis === 'height') {
        imgWidth = Math.min(photo.width, maxWidth);
        const scale = photo.width / imgWidth;
        imgHeight = Math.round(photo.height / scale);
    } else {
        imgHeight = Math.min(photo.height, maxHeight);
        const scale = photo.height / imgHeight;
        imgWidth = Math.round(photo.width / scale);
    }

    const showResponsive = responsiveIfBig && mobile && imgWidth >= 320;

    return (
        <Img
            className={cx(className, showResponsive && styles.BlogPostPhotos__responsiveImg)}
            width={showResponsive ? photo.width : imgWidth}
            height={showResponsive ? photo.height : imgHeight}
            src={photoUrl(photo, 's720x720')}
            lazy
        />
    );
};

const CompactPhoto = ({ complaintsCount, image, mobile, post }: { complaintsCount: number, image: ImageEntity, mobile: boolean, post: BlogPostEntity }) => {
    return (
        <Link className={styles.BlogPostPhotos__compactPhoto} to={photoDetailUrl(post, image)}>
            <ResizedPhoto
                className={styles.BlogPostPhotos__compactPhoto__img}
                maxHeight={140}
                maxWidth={500}
                mobile={mobile}
                photo={image.photo}
                responsiveIfBig={false}
            />

            {complaintsCount > 0 && (
                <div className={cx(styles.BlogPostPhotos__info, styles['BlogPostPhotos__info--complaints'])}>
                    <span className={styles.BlogPostPhotos__info__comments}>
                        {image.commentsCount}
                        {' '}
                        <i className="fa fa-comment-o" />
                    </span>
                </div>
            )}
        </Link>
    );
};

const NonCompactPhoto = ({ image, inFeed, mobile, post }: { image: ImageEntity, inFeed: boolean, mobile: boolean, post: BlogPostEntity }) => {
    return (
        <div className={styles.BlogPostPhotos__nonCompactPhoto}>
            <Link to={photoDetailUrl(post, image)}>
                <ResizedPhoto
                    className={styles.BlogPostPhotos__nonCompactPhoto__img}
                    maxHeight={MAX_HEIGHT}
                    maxWidth={MAX_WIDTH}
                    mobile={mobile}
                    photo={image.photo}
                    responsiveIfBig
                />
            </Link>

            {!inFeed && image.likesCount + image.commentsCount > 0 && (
                <div className={cx(styles.BlogPostPhotos__info, styles['BlogPostPhotos__info--no-complaints'])}>
                    {image.likesCount ? (
                        <span className={styles.BlogPostPhotos__info__likes}>
                            {image.likesCount}
                            {' '}
                            <i className="fa fa-thumbs-o-up" />
                        </span>
                    ) : null}
                    {image.commentsCount ? (
                        <span className={styles.BlogPostPhotos__info__comments}>
                            {image.commentsCount}
                            {' '}
                            <i className="fa fa-comment-o" />
                        </span>
                    ) : null}
                </div>
            )}

            {!inFeed && showMagnifier(image.photo) && (
                <div className={styles.BlogPostPhotos__controls}>
                    <a href={photoUrl(image.photo, 's1600x1600')} target="_blank">
                        <i className={cx('fa', 'fa-search-plus', styles.BlogPostPhotos__controls__control)} />
                    </a>
                </div>
            )}
        </div>
    );
};

export default class BlogPostPhotos extends React.Component<OwnProps> {

    public render() {
        const { complaintsCounts, inFeed, mobile, post, showStrip } = this.props;

        const images = post.images;

        if (images.length === 0) {
            return null;
        }

        const compact = inFeed && images.length > 1;

        return (
            <div className={styles.BlogPostPhotos}>
                {compact ? [
                    <div key="count" className={styles.BlogPostPhotos__count}>
                        ({interpolate(counter_PHOTOS, { count: images.length, counter: images.length })})
                    </div>,

                    <div key="container" className={showStrip ? styles.BlogPostPhotos__strip : styles.BlogPostPhotos__nonstrip}>
                        {images.map((image) => (
                            <CompactPhoto
                                key={image.id}
                                complaintsCount={complaintsCounts[image.id] || 0}
                                image={image}
                                mobile={mobile}
                                post={post}
                            />
                        ))}

                        {showStrip && (
                            <div className={styles.BlogPostPhotos__strip__mask} />
                        )}
                    </div>,
                ] : (
                    images.map((image) => (
                        <NonCompactPhoto
                            key={image.id}
                            image={image}
                            inFeed={inFeed}
                            mobile={mobile}
                            post={post}
                        />
                    ))
                )}
            </div>
        );
    }
}
